import { useState } from 'react';
import { HardDrive, MoreHorizontal, Plus } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useResizeDisk,
  useDetachDisk,
  useVolumes,
  useAttachVolume,
  useCreateVolume,
  VOLUME_TYPES,
  type Instance,
  type InstanceDisk,
  type Volume,
} from '@/api/opennebula';

const toGB = (mb?: number) => (mb ? Math.round((mb / 1024) * 10) / 10 : 0);

export function VolumeTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { show } = useToast();
  const disks = [...(instance.metadata?.disks ?? [])].sort(
    (a, b) => Number(a.disk_id) - Number(b.disk_id),
  );
  const [resizeDisk, setResizeDisk] = useState<InstanceDisk | null>(null);
  const [detachDisk, setDetachDisk] = useState<InstanceDisk | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);

  const detach = useDetachDisk(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume detached' });
      setDetachDisk(null);
    },
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  const columns: ColumnDef<InstanceDisk, unknown>[] = [
    {
      id: 'disk',
      header: 'Volume',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>disk-{row.original.disk_id}</span>,
    },
    {
      id: 'image',
      header: 'Image',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.image || '—'}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge tone={row.original.disk_id === '0' ? 'info' : 'neutral'} appearance="subtle" size="sm">
          {row.original.disk_id === '0' ? 'System' : 'Mount'}
        </Badge>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      cell: ({ row }) => <span>{toGB(row.original.size)} GB</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Volume actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setResizeDisk(row.original)}>Resize</DropdownMenuItem>
              {row.original.disk_id !== '0' && (
                <DropdownMenuItem tone="danger" onSelect={() => setDetachDisk(row.original)}>
                  Detach
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Volumes"
      description="Block storage volumes connected to this instance."
      actions={
        <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>
          Attach volume
        </Button>
      }
    >
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      <DataTable
        columns={columns}
        data={disks}
        getRowId={(d) => String(d.disk_id)}
        emptyState={
          <EmptyState
            icon={<HardDrive size={28} />}
            title="No volumes"
            description="This instance has no attached disks."
            actions={
              <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>
                Attach volume
              </Button>
            }
          />
        }
      />

      <AttachVolumeDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        serviceId={serviceId}
        instanceId={instance.id}
      />

      <ResizeDiskDialog
        serviceId={serviceId}
        instanceId={instance.id}
        disk={resizeDisk}
        onClose={() => setResizeDisk(null)}
      />

      <ConfirmDialog
        open={!!detachDisk}
        onOpenChange={(o) => !o && setDetachDisk(null)}
        title="Detach volume?"
        description={`Detach disk-${detachDisk?.disk_id} from this instance? Data on the volume is preserved.`}
        confirmLabel="Detach"
        destructive
        loading={detach.isPending}
        onConfirm={() =>
          detachDisk &&
          detach.mutate({
            // Path needs the disk's volume (image) id; body carries the disk index.
            volumeId: detachDisk.id ?? detachDisk.image_id ?? detachDisk.image ?? '',
            diskId: detachDisk.disk_id,
          })
        }
      />
    </Section>
  );
}

function AttachVolumeDialog({
  open,
  onOpenChange,
  serviceId,
  instanceId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  instanceId: string;
}) {
  const { show } = useToast();
  const { data: volumes = [], isLoading, refetch } = useVolumes(open ? serviceId : undefined);
  const [volumeId, setVolumeId] = useState('');
  // Mirror @console: either pick an existing standalone volume, or create a new
  // one inline (auto-selected on success) before attaching.
  const [isCreate, setIsCreate] = useState(false);
  const [name, setName] = useState('');
  const [size, setSize] = useState('5');
  const [type, setType] = useState<Volume['type']>('hiops');

  // Only standalone (unattached) volumes can be attached to this instance.
  const available = volumes.filter((v) => v.id && !v.attached);

  function reset() {
    setVolumeId('');
    setIsCreate(false);
    setName('');
    setSize('5');
    setType('hiops');
  }

  const create = useCreateVolume(serviceId, {
    onSuccess: async (res) => {
      // Legacy create returns `{ data: { result: <new volume id> } }` — refetch
      // the list and auto-select the new volume so the user can attach it.
      const newId = String(
        (res as { data?: { result?: string | number } } | undefined)?.data?.result ?? '',
      );
      await refetch();
      if (newId) setVolumeId(newId);
      setIsCreate(false);
      show({ tone: 'success', title: 'Volume created', description: 'Now attach it to the instance.' });
    },
    onError: () => show({ tone: 'danger', title: 'Could not create volume' }),
  });

  const attach = useAttachVolume(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume attach requested' });
      reset();
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });

  const sizeNum = Number(size);
  const createValid = !!name.trim() && sizeNum > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach volume</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            Select an existing standalone volume, or create a new one to attach to this instance.
            Halt any processes writing to the volume before attaching it elsewhere.
          </p>

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--vn-space-2)',
              marginBottom: 'var(--vn-space-3)',
              cursor: 'pointer',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            <Checkbox checked={isCreate} onCheckedChange={(v) => setIsCreate(v === true)} />
            Create a new volume — provision one if no existing volumes are ready to attach.
          </label>

          {isCreate ? (
            <div
              style={{
                display: 'grid',
                gap: 'var(--vn-space-3)',
                padding: 'var(--vn-space-3)',
                background: 'var(--vn-bg-muted)',
                borderRadius: 'var(--vn-radius-md)',
              }}
            >
              <Field label="Volume name">
                {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="data-volume" />}
              </Field>
              <Field label="Size (GiB)">
                {(p) => <Input {...p} type="number" min={1} value={size} onChange={(e) => setSize(e.target.value)} />}
              </Field>
              <Field label="Volume type">
                {(p) => (
                  <Select value={type} onValueChange={(v) => setType(v as Volume['type'])}>
                    <SelectTrigger {...p} style={{ width: '100%' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOLUME_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <div>
                <Button
                  disabled={!createValid || create.isPending}
                  onClick={() => create.mutate({ name: name.trim(), size: sizeNum, type })}
                >
                  {create.isPending ? 'Creating…' : 'Create volume'}
                </Button>
              </div>
            </div>
          ) : (
            <Field label="Volume">
              {(p) => (
                <Select value={volumeId} onValueChange={setVolumeId} disabled={isLoading || available.length === 0}>
                  <SelectTrigger {...p} style={{ width: '100%' }}>
                    <SelectValue
                      placeholder={
                        isLoading
                          ? 'Loading…'
                          : available.length
                            ? 'Select a volume'
                            : 'No standalone volumes — enable "Create a new volume"'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((v) => (
                      <SelectItem key={v.id} value={v.id!}>
                        {v.name || v.id} ({toGB(v.size)} GB)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!volumeId || isCreate || attach.isPending}
            onClick={() => attach.mutate({ instanceId, volumeId })}
          >
            {attach.isPending ? 'Attaching…' : 'Attach volume'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResizeDiskDialog({
  serviceId,
  instanceId,
  disk,
  onClose,
}: {
  serviceId: string;
  instanceId: string;
  disk: InstanceDisk | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const [size, setSize] = useState('');
  const current = toGB(disk?.size);

  const resize = useResizeDisk(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Resize requested' });
      onClose();
      setSize('');
    },
    onError: () => show({ tone: 'danger', title: 'Resize failed' }),
  });

  const next = Number(size);
  const valid = next > current;

  return (
    <Dialog open={!!disk} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resize disk-{disk?.disk_id}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field
            label={`New size (GB) — current ${current} GB`}
            required
            description="Volumes can only be grown, not shrunk."
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={current + 1}
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder={`> ${current}`}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid || next > 1000 || resize.isPending}
            onClick={() => disk && resize.mutate({ disk_id: disk.disk_id, size: Math.round(next) })}
          >
            Resize
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
